const CODE_ALPHABET = '23456789CFGHJMPQRVWX';
const PAIR_RESOLUTIONS = [20, 1, 0.05, 0.0025, 0.000125];
const SEPARATOR_POSITION = 8;

export interface PlusCodeCoordinate {
  latitude: number;
  longitude: number;
}

export function extractPlusCode(value: string): string | null {
  return value.toUpperCase().match(/[23456789CFGHJMPQRVWX]{2,8}\+[23456789CFGHJMPQRVWX]{2,7}/)?.[0] ?? null;
}

export function recoverPlusCode(code: string, referenceLatitude: number, referenceLongitude: number): PlusCodeCoordinate | null {
  const normalizedCode = code.toUpperCase();
  const separatorIndex = normalizedCode.indexOf('+');
  if (!isValidPlusCode(normalizedCode, separatorIndex)) {
    return null;
  }

  const isShortCode = separatorIndex < SEPARATOR_POSITION;
  if (isShortCode && (!Number.isFinite(referenceLatitude) || !Number.isFinite(referenceLongitude))) {
    return null;
  }

  const fullCode = isShortCode
    ? encodeReference(referenceLatitude, referenceLongitude).slice(0, SEPARATOR_POSITION - separatorIndex) + normalizedCode
    : normalizedCode;
  const coordinate = decodeFullPlusCode(fullCode);
  if (!coordinate) {
    return null;
  }

  if (!isShortCode) {
    return coordinate;
  }

  const resolution = Math.pow(20, 2 - ((SEPARATOR_POSITION - separatorIndex) / 2));
  const halfResolution = resolution / 2;
  let {latitude, longitude} = coordinate;

  if (referenceLatitude + halfResolution < latitude && latitude - resolution >= -90) {
    latitude -= resolution;
  } else if (referenceLatitude - halfResolution > latitude && latitude + resolution <= 90) {
    latitude += resolution;
  }
  if (referenceLongitude + halfResolution < longitude) {
    longitude -= resolution;
  } else if (referenceLongitude - halfResolution > longitude) {
    longitude += resolution;
  }

  return {
    latitude: roundCoordinate(latitude),
    longitude: roundCoordinate(longitude)
  };
}

function isValidPlusCode(code: string, separatorIndex: number): boolean {
  return separatorIndex >= 2
    && separatorIndex <= SEPARATOR_POSITION
    && separatorIndex % 2 === 0
    && code.indexOf('+') === code.lastIndexOf('+')
    && code.length - separatorIndex - 1 >= 2
    && /^[23456789CFGHJMPQRVWX+]+$/.test(code);
}

function encodeReference(latitude: number, longitude: number): string {
  let remainingLatitude = Math.min(89.999999, Math.max(-90, latitude)) + 90;
  let remainingLongitude = normalizeLongitude(longitude) + 180;
  let code = '';

  for (const resolution of PAIR_RESOLUTIONS) {
    const latitudeDigit = Math.floor(remainingLatitude / resolution);
    const longitudeDigit = Math.floor(remainingLongitude / resolution);
    code += CODE_ALPHABET[latitudeDigit] + CODE_ALPHABET[longitudeDigit];
    remainingLatitude -= latitudeDigit * resolution;
    remainingLongitude -= longitudeDigit * resolution;
  }

  return `${code.slice(0, SEPARATOR_POSITION)}+${code.slice(SEPARATOR_POSITION)}`;
}

function decodeFullPlusCode(code: string): PlusCodeCoordinate | null {
  const digits = code.replace('+', '');
  if (digits.length !== 10) {
    return null;
  }

  let latitude = -90;
  let longitude = -180;
  for (let index = 0; index < digits.length; index += 2) {
    const latitudeDigit = CODE_ALPHABET.indexOf(digits[index]);
    const longitudeDigit = CODE_ALPHABET.indexOf(digits[index + 1]);
    if (latitudeDigit < 0 || longitudeDigit < 0) {
      return null;
    }
    const resolution = PAIR_RESOLUTIONS[index / 2];
    latitude += latitudeDigit * resolution;
    longitude += longitudeDigit * resolution;
  }

  const precision = PAIR_RESOLUTIONS.at(-1)!;
  return {
    latitude: roundCoordinate(latitude + precision / 2),
    longitude: roundCoordinate(longitude + precision / 2)
  };
}

function normalizeLongitude(value: number): number {
  return ((value + 180) % 360 + 360) % 360 - 180;
}

function roundCoordinate(value: number): number {
  return Number(value.toFixed(7));
}
